import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { MultiSelect } from 'primereact/multiselect';
import axiosInstance from '../../utils/axiosInstance';
import { formatNumber, StatusEnum } from '../../assets/common';
import InputText from '../../components/InputText';
import Dropdown from '../../components/Dropdown';
import SenetGenerator from '../../utils/SenetGenerator.jsx';
import PaymentDialog from './PaymentDialog.jsx';
import PartialPaymentDialog from './PartialPaymentDialog.jsx';
import ActionDialog from './ActionDialog.jsx';
import { useLoading } from '../../components/Loading/LoadingProvider.jsx';
import { useConfirmDialog } from '../../utils/ConfirmDialog/ConfirmDialogProvider.jsx';
import { useAuth } from '../../utils/AuthProvider.jsx';
import toastService from '../../utils/ToastService.jsx';


const columns = [
    {
        expander: true,
        style: { text: "center", width: '8rem' },
        header: 'Ödeme Detayı',
    },
    {
        field: 'name',
        header: 'Müşteri',
        sortable: true,
        body: (rowData) => `${rowData.name || ''} ${rowData.surname || ''}`
    },
    {
        field: 'guarantorName',
        header: 'Kefil',
        sortable: true,
        body: (rowData) => `${rowData.guarantorName || ''} ${rowData.guarantorSurname || ''}`
    },
    {
        field: 'dateOfRecorded',
        header: 'Ödeme Kayıt Tarihi',
        sortable: true,
        body: (rowData) => <div className='text-center'>{rowData.dateOfRecorded ? new Date(rowData.dateOfRecorded).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}</div>
    },
    {
        field: 'dateOfFirstPay',
        header: 'İlk Ödeme Tarihi',
        sortable: true,
        body: (rowData) => <div className='text-center'>{rowData.dateOfFirstPay ? new Date(rowData.dateOfFirstPay).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}</div>
    },
    {
        field: 'term',
        header: 'Vade',
        sortable: true,
        body: (rowData) => <div className='text-center'>{rowData?.term} Ay</div>
    },
    {
        field: 'rate',
        header: 'Faiz Oranı',
        sortable: true,
        body: (rowData) => <div className='text-center'>% {rowData?.rate} (Aylık)</div>

    },
    {
        field: 'paymentType',
        header: 'Ödeme Türü',
        sortable: true,
        body: (rowData) => <div className='text-center'>{rowData?.paymentType?.value}</div>

    },
    {
        field: 'period',
        header: 'Değişim Periyodu',
        sortable: true,
        body: (rowData) => rowData?.period ? <div className='text-center'>{rowData?.period} Ay</div> : null

    },
    {
        field: 'amount',
        header: 'Ana Para',
        sortable: true,
        body: (rowData) => <div className='font-bold text-end'>{rowData?.amount ? formatNumber(rowData.amount) + " ₺" : ''}</div>
    },
    {
        field: 'totalInterestAmount',
        header: 'Toplam Faiz Tutarı',
        sortable: true,
        body: (rowData) => <div className='font-bold text-end'>{rowData?.totalInterestAmount ? formatNumber(rowData.totalInterestAmount) + " ₺" : ''}</div>

    },
    {
        field: 'totalRefundAmount',
        header: 'Toplam Tutar',
        sortable: true,
        body: (rowData) => <div className='font-bold text-end'>{rowData?.totalRefundAmount ? formatNumber(rowData.totalRefundAmount) + " ₺" : ''}</div>

    },

    {
        field: 'lastPaidInstallmentOrder',
        header: 'Son Ödenen Taksit',
        sortable: true,
        body: (rowData) => <div className='font-bold text-end'>{rowData?.lastPaidInstallmentOrder ? formatNumber(rowData.lastPaidInstallmentOrder) + " ₺" : ''}</div>

    },
    {
        field: 'lastPaidInstallmentAmount',
        header: 'Son Ödenen Tutar',
        sortable: true,
        body: (rowData) => <div className='font-bold text-end'>{rowData?.lastPaidInstallmentAmount ? formatNumber(rowData.lastPaidInstallmentAmount) + " ₺" : ''}</div>

    },
    {
        field: 'remark',
        header: 'Açıklama',
        sortable: true,
    },

];

const PayList = () => {
    const { setLoading } = useLoading();
    const { user } = useAuth();

    const [data, setData] = useState([]);

    const [expandedRows, setExpandedRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [visibleSidebar, setVisibleSidebar] = useState(false);
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        sortField: null,
        sortOrder: null,
    });
    const [visibleColumns, setVisibleColumns] = useState(columns);
    const [expandedRowData, setExpandedRowData] = useState({});
    const [expandedFilters, setExpandedFilters] = useState({});

    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedPaymentData, setSelectedPaymentData] = useState(null);
    const [selectedPaymentTrackingData, setSelectedPaymentTrackingData] = useState(null);


    const [partialPaymentDialogVisible, setPartialPaymentDialogVisible] = useState(false);

    const [actionDialogVisible, setActionDialogVisible] = useState(false);
    const [actionDialogActions, setActionDialogActions] = useState([]);
    const [actionDialogTitle, setActionDialogTitle] = useState("Ödeme Paneli");

    const { showDialog } = useConfirmDialog();

    useEffect(() => {
        onLoadData(lazyParams, appliedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams, appliedFilters]);

    const filterFields = [
        {
            field: 'seller',
            label: 'Satıcı',
            type: 'text',
        },
        {
            field: 'customerId',
            label: 'Müşteri ID',
            type: 'text',
        },
        {
            field: 'status',
            label: 'Durum',
            type: 'dropdown',
            options: StatusEnum,
            optionLabel: 'value',
            optionValue: 'id',
            placeholder: 'Durum Seçiniz',
        },
    ];

    const openPaymentPanel = (rowData) => {
        const actions = [];

        // Ödeme Tamamla
        if (rowData?.isPaid?.id === 0) {
            actions.push({
                label: "Ödeme Tamamla",
                icon: "pi pi-check",
                color: "green",
                onClick: () => {
                    setActionDialogVisible(false);
                    handlePaymentClick(rowData);
                }
            });
        }

        // Parçalı Ödeme
        if (rowData?.isPaid?.id === 0 && rowData?.isPartialPayment?.id === 0) {
            actions.push({
                label: "Parçalı Ödeme",
                icon: "pi pi-percentage",
                color: "blue",
                onClick: () => {
                    setActionDialogVisible(false);
                    handlePartialPayment(rowData);
                }
            });
        }

        // Ödemeyi Geri Al
        if (rowData?.isPaid?.id === 1) {
            actions.push({
                label: "Ödemeyi Geri Al",
                icon: "pi pi-refresh",
                color: "orange",
                onClick: () => showDialog({
                    message: "Taksit ödemesini geri almak istediğinize emin misiniz ?",
                    onConfirm: () => {
                        setActionDialogVisible(false);
                        rollBackPayment(rowData);
                    }
                })
            });
        }

        // Eğer hiç aksiyon yoksa, sadece bilgi göster
        if (actions.length === 0) {
            actions.push({
                label: "İşlem Yok",
                icon: "pi pi-ban",
                color: "blue",
                onClick: () => setActionDialogVisible(false)
            });
        }

        setActionDialogActions(actions);
        setActionDialogTitle("Ödeme Paneli");
        setActionDialogVisible(true);
    };

    const onColumnToggle = (event) => {
        let selectedColumns = event.value;
        let orderedSelectedColumns = columns.filter((col) =>
            selectedColumns.some((sCol) => sCol.field === col.field)
        );

        setVisibleColumns(orderedSelectedColumns);
    };

    const onPage = (event) => {
        setLazyParams({
            ...lazyParams,
            first: event.first,
            rows: event.rows,
            page: event.page,
        });
    };

    const onSort = (event) => {
        const isEnumField = columns.find(col => col.field === event.sortField)?.isEnum;
        setLazyParams({
            ...lazyParams,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            sortType: isEnumField ? 'enum_value' : 'default'
        });
    };

    const onFilterApply = () => {
        setAppliedFilters({ ...filters });
        setLazyParams({ ...lazyParams, first: 0, page: 0 });
        setVisibleSidebar(false);
    };

    const onFilterClear = () => {
        setFilters({});
        setAppliedFilters({});
        setLazyParams({ ...lazyParams, first: 0, page: 0 });
        setVisibleSidebar(false);
    };

    const rowClassName = (rowData) => {
        return {
            'row-active': rowData.status && rowData.status.id === 3,
            'row-inactive': !rowData.status || rowData.status.id !== 3,
        };
    };

    const onLoadData = async (lazyParams, filters) => {
        setLoading(true); // Global loading başlat
        const { first, rows, sortField, sortOrder } = lazyParams;
        const pageNumber = first / rows;
        const pageSize = rows;

        const requestBody = {
            filter: prepareFiltersForBackend(filters),
            pageNumber: pageNumber,
            pageSize: pageSize,
            sortField: sortField,
            sortOrder: sortOrder === 1 ? 'ASC' : sortOrder === -1 ? 'DESC' : null,
        };

        try {
            const response = await axiosInstance.post('/api/v1/payment/paged', requestBody);
            setData(response.data.content || []);
            setTotalRecords(response.data.totalElements || 0);
        } catch (error) {
            toastService.error(`Ödeme verisi getirilirken bir hata oluştu. ${error}`, "Hata")
            console.error('Veriler yüklenirken hata oluştu:', error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const prepareFiltersForBackend = (filters) => {
        filters.seller = user?.username
        const preparedFilters = {};

        for (const key in filters) {
            const filterValue = filters[key];
            if (filterValue) {
                if (Array.isArray(filterValue) && filterValue.length === 2) {
                    preparedFilters[key] = {
                        from: filterValue[0],
                        to: filterValue[1],
                    };
                } else if (typeof filterValue === 'object' && filterValue.id !== undefined) {
                    // Enum değeri için id'yi gönder
                    preparedFilters[key] = filterValue.id;
                } else {
                    preparedFilters[key] = filterValue;
                }
            }
        }
        return preparedFilters;
    };

    const onRowExpand = async (event) => {
        setLoading(true); // Global loading başlat
        try {
            const response = await axiosInstance.get(`/api/v1/payment/detail/${event.data.id}`);
            setExpandedRowData((prevData) => ({
                ...prevData,
                [event.data.id]: response.data,
            }));
            // Varsayılan olarak "Hepsi" seçeneğini belirlemek
            setExpandedFilters((prevFilters) => ({
                ...prevFilters,
                [event.data.id]: 'all',
            }));
        } catch (error) {
            toastService.error(`Ödeme detayı getirilirken bir hata oluştu. ${error}`, "Hata")
            console.error('Ödeme detayı yüklenirken hata oluştu:', error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const onFilterChange = (filterValue, rowId) => {
        setExpandedFilters((prevFilters) => ({
            ...prevFilters,
            [rowId]: filterValue,
        }));
    };

    const refreshRowData = async (paymentTrackingId, ids) => {
        setLoading(true); // Global loading başlat
        try {
            // ids parametresi array veya tekil id olabilir
            const idArray = Array.isArray(ids) ? ids : [ids];

            // Her id için sırayla istek gönder
            for (const id of idArray) {
                const response = await axiosInstance.get(`/api/v1/payment/getOneDetail/${id}`);
                const updatedDetail = response.data;

                setExpandedRowData((prevData) => {
                    return {
                        ...prevData,
                        [paymentTrackingId]: {
                            ...prevData[paymentTrackingId],
                            [id]: updatedDetail,
                        },
                    };
                });

                setExpandedRows((prevExpandedRows) => {
                    const currentExpandedRows = Array.isArray(prevExpandedRows) ? prevExpandedRows : [];
                    if (!currentExpandedRows.includes(id)) {
                        return [...currentExpandedRows, id];
                    }
                    return currentExpandedRows;
                });
            }
        } catch (error) {
            toastService.error(`Veri yenilenirken bir hata oluştu. ${error}`, "Hata")
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const rowExpansionTemplate = (data) => {
        const expandedData = expandedRowData[data.id];
        if (!expandedData) return null;

        const filterValue = expandedFilters[data.id] || 'all';

        // Önce status.id === 3 olanları filtrele
        const statusFiltered = Object.values(expandedData).filter(item =>
            item.status && item.status.id === 3
        );

        // Sonra diğer filtreleri uygula
        const filteredData = statusFiltered.filter(item => {
            if (filterValue === 'all') return true;
            if (!item.isPaid || typeof item.isPaid !== 'object') return false;
            if (filterValue === 'paid') return item.isPaid.id === 1;
            if (filterValue === 'unpaid') return item.isPaid.id === 0;
            return true;
        });

        const sortedData = filteredData.sort((a, b) => {
            const aPaid = a.isPaid?.id ?? -1;
            const bPaid = b.isPaid?.id ?? -1;

            // First sort by isPaid status (0 = unpaid first, 1 = paid later)
            if (aPaid !== bPaid) return aPaid - bPaid;

            // For unpaid items (isPaid.id === 0), sort by installmentDate
            if (aPaid === 0 && bPaid === 0) {
                const dateA = a.installmentDate ? new Date(a.installmentDate) : null;
                const dateB = b.installmentDate ? new Date(b.installmentDate) : null;

                if (dateA && dateB) return dateA - dateB;
                if (dateA) return -1;
                if (dateB) return 1;
            }

            // For paid items (isPaid.id === 1), sort by paidDate
            if (aPaid === 1 && bPaid === 1) {
                const paidDateA = a.paidDate ? new Date(a.paidDate) : null;
                const paidDateB = b.paidDate ? new Date(b.paidDate) : null;

                if (paidDateA && paidDateB) return paidDateA - paidDateB;
                if (paidDateA) return -1;
                if (paidDateB) return 1;
            }

            // If all else is equal, sort by installmentOrder and partialPaymentInstallmentOrder
            if (a.installmentOrder !== b.installmentOrder) {
                return a.installmentOrder - b.installmentOrder;
            }

            // For partial payments, sort by partialPaymentInstallmentOrder
            const aPartialOrder = a.partialPaymentInstallmentOrder || 0;
            const bPartialOrder = b.partialPaymentInstallmentOrder || 0;
            return aPartialOrder - bPartialOrder;
        });

        return (
            <div className='p-5 shadow-2xl'>
                <div className="mb-3 flex gap-4">
                    <div className='w-[200px]'>
                        <Dropdown
                            value={filterValue}
                            options={[
                                { label: 'Hepsi', value: 'all' },
                                { label: 'Ödenmiş', value: 'paid' },
                                { label: 'Ödenmemiş', value: 'unpaid' }
                            ]}
                            onChange={(e) => onFilterChange(e.value, data.id)}
                            placeholder="Filtre Seçiniz"
                        />
                    </div>
                    <SenetGenerator
                        user={user}
                        paymentData={data}
                        paymentDetails={sortedData}
                    />
                </div>
                <DataTable value={sortedData} paginator rows={5} emptyMessage="Detay bulunamadı."
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown" rowsPerPageOptions={[5, 10, 25]}
                    rowClassName={detailRowClassName}
                >
                    <Column
                        header="Ödeme Paneli"
                        body={(rowData) => (
                            <div className="flex justify-center items-center">
                                <Button
                                    label="Ödeme Paneli"
                                    icon="pi pi-credit-card"
                                    onClick={() => openPaymentPanel(rowData)}
                                    className={
                                        "rounded-xl shadow-lg cursor-pointer border font-semibold px-6 py-1 text-sm " +
                                        getPaymentPanelButtonClass(rowData)
                                    }
                                />
                            </div>
                        )}
                    />
                    <Column field="installmentOrder"
                        header="Taksit Sırası"
                        sortable
                        body={(rowData) => (
                            <div className="font-bold text-center">
                                {rowData?.isPartialPayment?.id === 0
                                    ? `${rowData?.installmentOrder}.`
                                    : `${rowData?.installmentOrder} - ${rowData?.partialPaymentInstallmentOrder}.`}
                            </div>
                        )}

                    />
                    <Column field="dateOfPay" header="Taksit Tarihi" sortable body={(rowData) =>
                        <div className='text-center'>{rowData.installmentDate ? new Date(rowData.installmentDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}</div>
                    } />
                    <Column field="totalAmountOfInstallments"
                        header="Taksit Tutarı"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.totalAmountOfInstallments ? formatNumber(rowData.totalAmountOfInstallments) + " ₺" : ''}</div>}
                    />
                    <Column
                        field="installmentPrincipalAmount"
                        header="Taksit Anapara Tutarı"
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.installmentPrincipalAmount ? formatNumber(rowData.installmentPrincipalAmount) + " ₺" : ''}</div>}
                    />
                    <Column
                        field="installmentInterestAmount"
                        header="Taksit Faiz Tutarı"
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.installmentInterestAmount ? formatNumber(rowData.installmentInterestAmount) + " ₺" : ''}</div>}
                    />
                    <Column field="isPaid" header="Ödeme Durumu" sortable body={(rowData) => <div className='text-center'>{rowData.isPaid && rowData.isPaid.id === 1 ? "Tamamlandı" : "Tamamlanmadı"}</div>} />
                    <Column field="remainingTotalPaymentAmount"
                        header="Kalan Toplam Borç Tutarı"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.remainingTotalPaymentAmount ? formatNumber(rowData.remainingTotalPaymentAmount) + " ₺" : '0 ₺'}</div>}
                    />
                    <Column field="remainingTotalPrincipalAmount"
                        header="Kalan Toplam Anapara Tutarı"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.remainingTotalPrincipalAmount ? formatNumber(rowData.remainingTotalPrincipalAmount) + " ₺" : '0 ₺'}</div>}
                    />
                    <Column field="kalanfaiztutari"
                        header="Kalan Toplam Faiz Tutarı"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.remainingTotalInterestAmount ? formatNumber(rowData.remainingTotalInterestAmount) + " ₺" : '0 ₺'}</div>}
                    />
                    <Column field="paidDate"
                        header="Ödeme Tamamlama Tarihi"
                        sortable
                        body={(rowData) => (
                            rowData?.isPaid?.id === 1 ?
                                <div className='flex justify-center items-center'>
                                    {rowData?.paidDate ? new Date(rowData.paidDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                                </div> : " "
                        )}
                    />
                    <Column field="amountPaid"
                        header="Ödeme Yapılan Tutar"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.amountPaid ? formatNumber(rowData?.amountPaid) + " ₺" : ''}</div>}
                    />
                    <Column field="latePaymentInterest"
                        header="Eklenen Gecikme Faizi"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.latePaymentInterest ? formatNumber(rowData?.latePaymentInterest) + " ₺" : ''}</div>}
                    />
                    <Column field="interestRateCut"
                        header="Erken Ödeme İndirimi"
                        sortable
                        body={(rowData) => <div className='font-bold text-end'>{rowData?.interestRateCut ? formatNumber(rowData?.interestRateCut) + " ₺" : ''}</div>}
                    />
                </DataTable>
            </div>
        );
    };

    const handlePartialPayment = (paymentDetails) => {
        // Taksit detayını ayarla
        setSelectedPaymentData(paymentDetails);

        // Üst tablo verisini bul ve ayarla
        // paymentTrackingId, taksit detayında bulunuyor ve üst tablodaki id'ye eşit
        const parentData = data.find(item => item.id === paymentDetails.paymentTrackingId);
        setSelectedPaymentTrackingData(parentData);

        // Dialog'u göster
        setPartialPaymentDialogVisible(true);
    };

    const confirmPartialPayment = async (partialPaymentData) => {
        setLoading(true); // Global loading başlat
        try {
            const response = await axiosInstance.put(`/api/v1/payment/partial-paid/${partialPaymentData.originalInstallmentId}`,
                partialPaymentData
            );
            setPartialPaymentDialogVisible(false);
            toastService.success('Parçalı ödeme başarılı bir şekilde kaydedildi.', "Parçalı ödeme başarılı")

            if (partialPaymentData.paymentTrackingId) {
                const ids = Array.isArray(response.data)
                    ? response.data.map(item => item.id)
                    : response.data.id;

                await refreshRowData(partialPaymentData.paymentTrackingId, ids);
            }
        } catch (error) {
            toastService.error(`Parçalı ödeme işlemi sırasında bir hata oluştu. ${error}`, "Hata")

        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const detailRowClassName = (rowData) => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Remove time component for comparison

        // Use installmentDate which is what's displayed in the table
        const paymentDate = rowData.installmentDate ? new Date(rowData.installmentDate) : null;
        if (paymentDate) paymentDate.setHours(0, 0, 0, 0);

        if (rowData.isPaid && rowData.isPaid.id === 1) {
            return 'payment-paid-row';
        } else if (paymentDate) {
            // Check if the date is today (comparing just the date part)
            const isToday =
                paymentDate.getDate() === currentDate.getDate() &&
                paymentDate.getMonth() === currentDate.getMonth() &&
                paymentDate.getFullYear() === currentDate.getFullYear();

            if (isToday) {
                return 'payment-today-row';
            } else if (paymentDate < currentDate) {
                return 'payment-overdue-row';
            } else {
                return 'payment-future-row';
            }
        }
        return '';
    };

    const getPaymentPanelButtonClass = (rowData) => {
        // detailRowClassName ile aynı mantık
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const paymentDate = rowData.installmentDate ? new Date(rowData.installmentDate) : null;
        if (paymentDate) paymentDate.setHours(0, 0, 0, 0);

        if (rowData.isPaid && rowData.isPaid.id === 1) {
            // Ödendi (yeşil)
            return "bg-green-100 text-green-800 border-green-400 hover:bg-green-200 hover:text-green-900";
        } else if (paymentDate) {
            const isToday =
                paymentDate.getDate() === currentDate.getDate() &&
                paymentDate.getMonth() === currentDate.getMonth() &&
                paymentDate.getFullYear() === currentDate.getFullYear();

            if (isToday) {
                // Bugün (turuncu)
                return "bg-orange-100 text-orange-800 border-orange-400 hover:bg-orange-200 hover:text-orange-900";
            } else if (paymentDate < currentDate) {
                // Gecikmiş (kırmızı)
                return "bg-red-100 text-red-800 border-red-400 hover:bg-red-200 hover:text-red-900";
            } else {
                // Gelecek (default mavi)
                return "bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200 hover:text-blue-900";
            }
        }
        // Default
        return "bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200 hover:text-blue-900";
    };

    const header = (
        <div className="flex justify-between align-center">
            <h5 className="m-0">Ödeme Listesi</h5>
            <span className="p-input-icon-left">
                <Button
                    type="button"
                    label="Filtreler"
                    icon="pi pi-filter"
                    onClick={() => setVisibleSidebar(true)}
                />
            </span>
        </div>
    );

    const handlePaymentClick = (rowData) => {
        setSelectedPaymentData(rowData);
        setDialogVisible(true);
    };

    const confirmPayment = async (paymentDetails) => {
        setLoading(true); // Global loading başlat
        try {
            // Updated API call with additional parameters
            const response = await axiosInstance.put(`/api/v1/payment/paid/${paymentDetails.id}`, {
                amountPaid: paymentDetails.amountPaid,
                latePaymentInterest: paymentDetails.latePaymentInterest,
                interestRateCut: paymentDetails.interestRateCut,
                paymentType: paymentDetails.paymentType
            });

            const paymentTrackingId = response.data.paymentTrackingId;
            toastService.success('Ödeme bilgisi başarılı bir şekilde güncellendi.', "Ödeme Başarılı")

            // Close the dialog
            setDialogVisible(false);

            // Refresh row data after a successful update
            await refreshRowData(paymentTrackingId, paymentDetails.id);
        } catch (error) {
            toastService.error(`Ödeme güncellenirken bir hata oluştu. ${error}`, "Hata")

        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const rollBackPayment = async (paymentDetails) => {
        setLoading(true);
        try {
            const response = await axiosInstance.put(`/api/v1/payment/unpaid/${paymentDetails.id}`)
            const paymentTrackingId = response.data.paymentTrackingId;
            setDialogVisible(false);
            await refreshRowData(paymentTrackingId, paymentDetails.id);
        } catch (error) {
            toastService.error(`Ödeme geri alınırken bir hata oluştu. ${error}`, "Hata")

        } finally {
            setTimeout(() => {
                setLoading(false);
                toastService.error('Taksit ödemesi başarılı bir şekilde eski haline getirildi.', "Ödeme Geri Alma Başarılı")
            }, 1 * 1000);
        }
    };

    return (
        <div>
            <PartialPaymentDialog
                visible={partialPaymentDialogVisible}
                onHide={() => setPartialPaymentDialogVisible(false)}
                paymentData={selectedPaymentData}
                paymentTrackingData={selectedPaymentTrackingData}
                onConfirm={confirmPartialPayment}
            />
            <PaymentDialog
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                paymentData={selectedPaymentData}
                onConfirm={confirmPayment}
            />
            <ActionDialog
                visible={actionDialogVisible}
                onHide={() => setActionDialogVisible(false)}
                actions={actionDialogActions}
                title={actionDialogTitle}
            />
            <Sidebar
                header={<h1>Filtreler</h1>}
                visible={visibleSidebar}
                position="left"
                onHide={() => setVisibleSidebar(false)}
                className="p-sidebar-sm"
            >
                <form className="p-fluid space-y-10 mt-10">
                    {filterFields.map((field) => {
                        switch (field.type) {
                            case 'text':
                                return (
                                    <div key={field.field} className="p-field">
                                        <InputText
                                            id={field.field}
                                            name={field.field}
                                            value={filters[field.field] || ''}
                                            onChange={(e) => {
                                                setFilters({ ...filters, [field.field]: e.target.value });
                                            }}
                                            label={field.label}
                                            type={field.type}
                                            clearFunction={() => {
                                                setFilters({ ...filters, [field.field]: '' });
                                            }}
                                        />
                                    </div>
                                );
                            case 'dropdown':
                                return (
                                    <div key={field.field} className="p-field">
                                        <Dropdown
                                            id={field.field}
                                            name={field.field}
                                            value={filters[field.field] || null}
                                            options={field.options}
                                            onChange={(e) => {
                                                setFilters({ ...filters, [field.field]: e.value });
                                            }}
                                            placeholder={field.placeholder || 'Seçiniz'}
                                            optionLabel={field.optionLabel || 'label'}
                                            optionValue={field.optionValue || 'value'}
                                            label={field.label}
                                            showClear={true}
                                            clearFunction={() => {
                                                setFilters({ ...filters, [field.field]: null });
                                            }}
                                        />
                                    </div>
                                );
                            default:
                                return null;
                        }
                    })}
                    <div className="flex justify-end gap-4 mt-4">
                        <Button
                            type="button"
                            label="Temizle"
                            icon="pi pi-times"
                            onClick={onFilterClear}
                            className="rounded-lg shadow-lg cursor-pointer text-red-700 hover:text-white border border-red-700 hover:bg-red-800 p-3"
                        />
                        <Button
                            type="button"
                            label="Filtrele"
                            icon="pi pi-filter"
                            onClick={onFilterApply}
                            className="rounded-lg shadow-lg cursor-pointer text-yellow-700 hover:text-white border border-yellow-700 hover:bg-yellow-800 p-3"
                        />
                    </div>
                </form>
            </Sidebar>

            <DataTable
                className="custom-datatable"
                header={header}
                value={data}
                lazy
                paginator
                totalRecords={totalRecords}
                first={lazyParams.first}
                rows={lazyParams.rows}
                onPage={onPage}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                responsiveLayout="scroll"
                emptyMessage="Kayıt bulunamadı."
                rowClassName={rowClassName}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown " rowsPerPageOptions={[5, 10, 25, 50]}
                removableSort
                paginatorLeft={
                    <MultiSelect
                        value={visibleColumns}
                        options={columns}
                        optionLabel="header"
                        onChange={onColumnToggle}
                        className="w-full text-sm max-w-60"
                        display="comma"
                    />
                }
                paginatorRight={<><strong>Toplam Kayıt Sayısı:</strong> {totalRecords}</>}
                stripedRows
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                onRowExpand={onRowExpand}
                // onRowCollapse={(e) => toast.current?.show({ severity: 'success', summary: 'Ödeme Daraltıldı', detail: e.data.customerId, life: 3000 })}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
            >
                {visibleColumns.map((col) => (
                    <Column
                        key={col.field}
                        {...col}
                        sortField={col.isEnum ? `${col.field}_value` : col.field}
                    />
                ))}
            </DataTable>
        </div>
    );
};

PayList.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            sortable: PropTypes.bool,
            body: PropTypes.func,
            sortField: PropTypes.string,
        })
    ).isRequired,
    filterFields: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(['text', 'number', 'date', 'dropdown']).isRequired,
            options: PropTypes.array,
            placeholder: PropTypes.string,
            optionLabel: PropTypes.string,
            optionValue: PropTypes.string,
        })
    ),
    data: PropTypes.array.isRequired,
    totalRecords: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    onLoadData: PropTypes.func.isRequired,
};

export default PayList;